import { useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Panel } from "@/components/app/common";
import { useMe } from "@/components/app/MeContext";
import { api, errMsg } from "@/lib/api";
import { fmtDate, inr } from "@/lib/format";

// `_k` is a UI-only row key; the API model ignores it.
const blank = () => ({ _k: crypto.randomUUID(), date: "", value: null });
const planned = (rows) => rows.reduce((s, r) => s + (Number(r.value) || 0), 0);

// Single invoice (one date, full LM value) or several dated part-invoices; the monthly invoicing plan reads these rows.
export function InvoicePlanEditor({ mode, rows, onChange, basic }) {
  const list = rows.length ? rows : [{ _k: "first", date: "", value: null }];
  const setRow = (i, k, v) => onChange(mode, list.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const multi = mode === "multiple";
  return (
    <div className="space-y-3" data-testid="lm-invoice-plan-editor">
      <div className="inline-flex rounded border border-slate-200 bg-slate-50 p-0.5">
        {[["single", "Single invoice"], ["multiple", "Multiple invoices"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => onChange(k, k === "single" ? list.slice(0, 1) : list)} data-testid={`lm-invoicing-${k}`}
            className={`px-3 py-1 text-xs rounded transition-colors ${mode === k ? "bg-white shadow-sm font-semibold text-slate-900" : "text-slate-500"}`}>{l}</button>
        ))}
      </div>
      {list.map((r, i) => (
        <div key={r._k || `row-${i}`} className="flex flex-wrap items-end gap-2" data-testid={`lm-invoice-row-${i}`}>
          <div><Label>Invoice date {multi ? i + 1 : ""}</Label><Input type="date" value={r.date || ""} onChange={(e) => setRow(i, "date", e.target.value)} className="w-44" data-testid={`lm-invoice-date-${i}`} /></div>
          <div><Label>Value {multi ? i + 1 : ""} (basic)</Label>
            {multi ? <Input type="number" min="0" value={r.value ?? ""} onChange={(e) => setRow(i, "value", e.target.value === "" ? null : Number(e.target.value))} className="w-40" data-testid={`lm-invoice-value-${i}`} />
              : <div className="h-9 w-40 flex items-center rounded border border-slate-200 bg-slate-50 px-3 text-sm font-mono text-slate-500">{basic ? inr(basic) : "Full LM value"}</div>}</div>
          {multi && list.length > 1 && <Button size="icon" variant="ghost" onClick={() => onChange(mode, list.filter((_, j) => j !== i))} data-testid={`lm-invoice-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>}
        </div>
      ))}
      {multi && (
        <div className="flex flex-wrap items-center gap-3">
          <Button size="sm" variant="outline" onClick={() => onChange(mode, [...list, blank()])} data-testid="lm-invoice-add-btn"><Plus className="h-4 w-4 mr-1" />Add invoice</Button>
          <span className={`text-xs ${basic && Math.abs(planned(list) - basic) > 1 ? "text-amber-700" : "text-slate-500"}`} data-testid="lm-invoice-plan-total">Planned {inr(planned(list))}{basic ? ` of ${inr(basic)} LM basic value` : ""} — must match before submitting</span>
        </div>
      )}
    </div>
  );
}

function PlanDialog({ order, open, onOpenChange, onDone }) {
  const [p, setP] = useState({ mode: order.invoicing_mode || "single", rows: (order.invoice_plan || []).map((r) => ({ ...r, _k: crypto.randomUUID() })) });
  const [reason, setReason] = useState("");
  const save = async () => {
    try { await api.post(`/orders/${order.id}/invoice-plan`, { invoicing_mode: p.mode, invoice_plan: p.rows, reason }); toast.success("Invoice plan saved"); onOpenChange(false); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="lm-invoice-plan-dialog">
        <DialogHeader><DialogTitle>Invoicing plan</DialogTitle></DialogHeader>
        <InvoicePlanEditor mode={p.mode} rows={p.rows} basic={order.subtotal} onChange={(mode, rows) => setP({ mode, rows })} />
        {order.status !== "draft" && <div><Label>Reason *</Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="lm-invoice-plan-reason" /></div>}
        <DialogFooter><Button onClick={save} data-testid="lm-invoice-plan-save">Save plan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function InvoicePlanPanel({ order, reload }) {
  const { can } = useMe();
  const [open, setOpen] = useState(false);
  const rows = order.invoice_plan || [];
  const live = !["closed", "cancelled"].includes(order.status);
  return (
    <Panel title={`Invoicing — ${order.invoicing_mode === "multiple" ? "multiple invoices" : "single invoice"}`} testid="lm-invoice-plan-panel"
      actions={can("write") && live && <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="lm-invoice-plan-edit-btn"><Pencil className="h-4 w-4 mr-1" />Change plan</Button>}>
      <div className="space-y-1 text-sm">
        {rows.map((r, i) => <div key={`${r.date}-${r.value}-${i}`} className="flex justify-between border-b border-slate-100 pb-1" data-testid={`lm-invoice-plan-row-${i}`}><span>Invoice {i + 1} · {fmtDate(r.date)}</span><span className="font-mono">{inr(r.value ?? order.subtotal)}</span></div>)}
        {!rows.length && <div className="text-xs text-slate-400">No invoice dates planned yet</div>}
      </div>
      {open && <PlanDialog order={order} open={open} onOpenChange={setOpen} onDone={reload} />}
    </Panel>
  );
}
